/**
 * useAuth Hook
 *
 * Custom hook providing a clean interface for Auth0 authentication.
 * Wraps the Auth0 React SDK with simplified methods for login, logout,
 * signup, and token retrieval.
 *
 * Desktop users get popup-based login for a seamless experience.
 * Mobile users and popup-blocked scenarios fall back to redirect.
 * Exposes isAuthenticating state so the UI can show an overlay while
 * the popup is open.
 */

import { useState, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { auth0Config } from "../config/auth0";
import type { AuthUser } from "../types/auth";

/** Simple mobile detection via viewport width and user-agent heuristic. */
function isMobileDevice(): boolean {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  const mobileUa = /android|iphone|ipad|ipod|mobile/i.test(ua);
  return mobileUa || window.innerWidth < 768;
}

export function useAuth() {
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    loginWithRedirect,
    loginWithPopup,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const [isAuthenticating, setIsAuthenticating] = useState(false);

  /**
   * Desktop: open Auth0 in a popup so the user stays on the current page.
   * Falls back to redirect if the popup is blocked.
   * Mobile: always redirect (popups are unreliable on mobile browsers).
   */
  const login = useCallback(async () => {
    if (isMobileDevice()) {
      await loginWithRedirect();
      return;
    }

    setIsAuthenticating(true);
    try {
      await loginWithPopup();
    } catch (err: unknown) {
      // If the user closed the popup, Auth0 throws "Popup closed".
      // That's a no-op — don't show an error, just reset state.
      const message = err instanceof Error ? err.message : String(err);
      if (/popup closed/i.test(message)) {
        setIsAuthenticating(false);
        return;
      }
      // Any other error (e.g. popup blocked) → fall back to redirect.
      setIsAuthenticating(false);
      await loginWithRedirect();
      return;
    }
    setIsAuthenticating(false);
  }, [loginWithPopup, loginWithRedirect]);

  /**
   * Same popup/redirect strategy, but passes screen_hint: "signup"
   * so Auth0 shows the signup tab.
   */
  const signUp = useCallback(async () => {
    const authorizationParams = { screen_hint: "signup" as const };

    if (isMobileDevice()) {
      await loginWithRedirect({ authorizationParams });
      return;
    }

    setIsAuthenticating(true);
    try {
      await loginWithPopup({ authorizationParams });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      if (/popup closed/i.test(message)) {
        setIsAuthenticating(false);
        return;
      }
      setIsAuthenticating(false);
      await loginWithRedirect({ authorizationParams });
      return;
    }
    setIsAuthenticating(false);
  }, [loginWithPopup, loginWithRedirect]);

  const logout = useCallback(() => {
    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }, [auth0Logout]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    return getAccessTokenSilently({
      authorizationParams: {
        audience: auth0Config.authorizationParams.audience,
      },
    });
  }, [getAccessTokenSilently]);

  return {
    user: user as AuthUser | undefined,
    isAuthenticated,
    isLoading,
    isAuthenticating,
    error,
    login,
    signUp,
    logout,
    getAccessToken,
    /** Exposed for ProtectedRoute — always does a full-page redirect. */
    loginWithRedirect,
  };
}
